import { useEffect, useRef, useState } from 'react'
import { X, Camera } from 'lucide-react'
import type { WireArticleBlock } from '../../../api/types'
import { useArticleArtifacts } from '../common/ArticleArtifactsContext'
import { useVisualEngagementContext } from '../common/VisualEngagementContext'
import { emitVisualInteraction, useVisualInViewTracking } from '../common/visualCollectionTracking'
import { PortionMark } from '../common/PortionMark'

type Block = Extract<WireArticleBlock, { type: 'photo' }>

// ─── Width → wrapper class ─────────────────────────────────────────────────────
// The outer ArticleRenderer container is max-w-5xl + px-8; 'full' fills it,
// 'wide' sits between column and full, 'column' matches the reading column.
const WIDTH_CLASS: Record<NonNullable<Block['width']>, string> = {
  'column':      'mx-auto max-w-3xl',
  'wide':        'mx-auto max-w-4xl',
  'full':        'w-full',
  'bleed':       'w-screen ml-[calc(50%-50vw)]',
  'float-left':  'float-left mr-6 mb-4 w-[38%] clear-left',
  'float-right': 'float-right ml-6 mb-4 w-[38%] clear-right',
}

export function PhotoBlock({ block }: { block: Block }) {
  const artifacts = useArticleArtifacts()
  const engagement = useVisualEngagementContext()
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const figureRef = useRef<HTMLElement>(null)
  const wasLightboxOpenRef = useRef(false)

  const { photo, width = 'column', aspectRatio = 'natural' } = block

  const imgSrc = artifacts.find(a => a.id === photo.artifactId)?.presignedUrl ?? ''

  const imgStyle: React.CSSProperties = {
    ...(aspectRatio !== 'natural' ? { aspectRatio: aspectRatio.replace(':', ' / ') } : {}),
    ...(photo.focalPoint
      ? { objectPosition: `${photo.focalPoint.x * 100}% ${photo.focalPoint.y * 100}%` }
      : {}),
  }

  const wrapperClass = WIDTH_CLASS[width]
  const visualId = photo.artifactId || block.id

  useVisualInViewTracking(engagement, figureRef, visualId, 'IMAGE')

  useEffect(() => {
    if (!engagement?.enabled || !photo.expandable) return
    if (lightboxOpen && !wasLightboxOpenRef.current) {
      emitVisualInteraction(engagement, visualId, 'IMAGE', 'LIGHTBOX_OPEN')
    }
    if (!lightboxOpen && wasLightboxOpenRef.current) {
      emitVisualInteraction(engagement, visualId, 'IMAGE', 'LIGHTBOX_CLOSE')
    }
    wasLightboxOpenRef.current = lightboxOpen
  }, [engagement, lightboxOpen, photo.expandable, visualId])

  const imgEl = (
    <img
      src={imgSrc}
      alt={photo.altText ?? ''}
      className="w-full rounded object-cover"
      style={imgStyle}
    />
  )

  return (
    <>
      <figure ref={figureRef} className={`my-0 ${wrapperClass}`}>
        {photo.expandable ? (
          <button
            type="button"
            onClick={() => {
              emitVisualInteraction(engagement, visualId, 'IMAGE', 'CLICK')
              setLightboxOpen(true)
            }}
            onPointerEnter={() => emitVisualInteraction(engagement, visualId, 'IMAGE', 'HOVER_OVER')}
            className="block w-full cursor-zoom-in rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary-solid"
            aria-label="Expand image"
          >
            {imgEl}
          </button>
        ) : (
          imgEl
        )}
        <figcaption className="mt-1.5 flex flex-wrap items-center gap-x-1.5 text-[11px] text-fg-secondary">
          <Camera size={10} aria-hidden="true" />
          <PortionMark classification={photo.classification} disseminationControls={photo.disseminationControls} />
          {photo.credit && <><span aria-hidden="true">·</span><span className="italic opacity-70">{photo.credit}</span></>}
          {photo.caption && <><span aria-hidden="true">·</span><PortionMark classification={photo.captionClassification} disseminationControls={photo.captionDisseminationControls} />{photo.caption}</>}
        </figcaption>
      </figure>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
            aria-label="Close"
          >
            <X size={18} />
          </button>
          <img
            src={imgSrc}
            onClick={(e) => e.stopPropagation()}
          />
          {(photo.caption || photo.credit) && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded bg-black/60 px-4 py-2 text-center text-xs text-white">
              {photo.caption && (
                <p><PortionMark classification={photo.captionClassification} disseminationControls={photo.captionDisseminationControls} />{photo.caption}</p>
              )}
              {photo.credit && <p className="mt-0.5 italic opacity-70">{photo.credit}</p>}
            </div>
          )}
        </div>
      )}
    </>
  )
}
